// src/api/redress.ts
//
// Typed HTTP client for the FCA Redress Scheme admin endpoints (Phase 2).
// Reuses the shared axios instance from ``api/claims.ts`` so auth headers,
// base URL, and 401-handling come for free.

import { client } from './claims';

// --- Types ------------------------------------------------------------------

export type RedressScheme = 'scheme_1' | 'scheme_2' | 'out_of_scope';

export type RedressStatus =
  | 'invited'
  | 'joined'
  | 'awaiting_prd'
  | 'prd_received'
  | 'prd_sent_to_client'
  | 'client_accepted'
  | 'client_objecting'
  | 'objection_submitted'
  | 'rd_sent_to_client'
  | 'awaiting_rd'
  | 'fd_received'
  | 'paid'
  | 'held_high_value'
  | 'opted_out'
  | 'rd_no_response_closed'
  | 'closed_no_commission'
  | 'closed_no_arrangement'
  | 'closed_no_unfair_relationship'
  | 'closed_no_loss'
  | 'closed_not_scheme_case'
  | 'closed_outside_subject_matter'
  | 'closed_exception_exists'
  | 'rejected_fos_referral';

// --- State machine mirror (P2-13 / H10) ---------------------------------------
// Mirror of ALLOWED_TRANSITIONS in
// python-backend/app/services/redress_service.py — KEEP IN SYNC. The
// OffersScreen action gates derive from this map so a button is enabled
// exactly when the server would accept the transition (a drifted ad-hoc
// gate previously produced buttons that 409'd 100% of the time).

const CLOSURE_STATUSES: ReadonlyArray<RedressStatus> = [
  'closed_no_commission',
  'closed_no_arrangement',
  'closed_no_unfair_relationship',
  'closed_no_loss',
  'closed_not_scheme_case',
  'closed_outside_subject_matter',
  'closed_exception_exists',
];

export const ALLOWED_TRANSITIONS: Record<
  RedressStatus,
  ReadonlyArray<RedressStatus>
> = {
  invited: ['joined', 'held_high_value', ...CLOSURE_STATUSES],
  joined: ['awaiting_prd', 'held_high_value', ...CLOSURE_STATUSES],
  awaiting_prd: ['prd_received', 'held_high_value', ...CLOSURE_STATUSES],
  prd_received: ['prd_sent_to_client', 'held_high_value', ...CLOSURE_STATUSES],
  held_high_value: ['awaiting_prd', 'prd_received', ...CLOSURE_STATUSES],
  prd_sent_to_client: ['client_objecting', 'awaiting_rd', 'client_accepted'],
  client_accepted: ['paid', 'fd_received'],
  // Batch-G review (fix 2): the lender may issue its RD directly from
  // client_objecting — CONRED 5.4.37R(4)(c)(ii): the formal-objection
  // window expired with no formal objection, so objection_submitted is
  // never reached. Mirrors the backend edge used by mark_rd_sent.
  client_objecting: ['objection_submitted', 'rd_sent_to_client'],
  objection_submitted: ['rd_sent_to_client', 'prd_sent_to_client', 'fd_received'],
  awaiting_rd: ['rd_sent_to_client'],
  rd_sent_to_client: [
    'client_accepted',
    'rejected_fos_referral',
    'opted_out',
    'rd_no_response_closed',
  ],
  fd_received: ['paid', 'rejected_fos_referral'],
  // Terminal statuses have no outgoing transitions.
  paid: [],
  closed_no_commission: [],
  closed_no_arrangement: [],
  closed_no_unfair_relationship: [],
  closed_no_loss: [],
  rejected_fos_referral: [],
  closed_not_scheme_case: [],
  closed_outside_subject_matter: [],
  closed_exception_exists: [],
  opted_out: [],
  rd_no_response_closed: [],
};

/** True when the backend state machine allows ``from -> to``. */
export function canTransition(from: RedressStatus, to: RedressStatus): boolean {
  return (ALLOWED_TRANSITIONS[from] ?? []).includes(to);
}

// --- Six-gate liability model -----------------------------------------------

export type NotSchemeCaseReason =
  | 'out_of_scheme_date_range'
  | 'not_regulated'
  | 'no_commission_arrangements'
  | 'no_commission_payable'
  | 'high_value_threshold'
  | 'cooling_off_cancellation'
  | 'limitation_expired'
  | 'prior_redress_accepted'
  | 'full_and_final_settlement'
  | 'fos_already_determined'
  | 'court_or_tribunal_decided';

export type SubjectMatterExclusion =
  | 'adequate_disclosure_dca'
  | 'adequate_disclosure_commission'
  | 'adequate_disclosure_tied'
  | 'adequate_disclosure_other_broker_incentive';

export type FairnessReason =
  | 'adequate_disclosure'
  | 'consumer_sophisticated'
  | 'tied_arrangement_not_operated_in_practice';

export interface GateSchemeCase {
  isSchemeCase?: boolean | null;
  notSchemeCaseReason?: NotSchemeCaseReason | null;
}
export interface GateSubjectMatter {
  inSubjectMatter?: boolean | null;
  exclusion?: SubjectMatterExclusion | null;
}
export interface GateRelevantArrangement {
  dca?: boolean;
  highCommission?: boolean;
  tied?: boolean;
  totalCommissionPaid?: number | null;
}
export interface GateExceptions {
  deMinimis?: boolean;
  zeroApr?: boolean;
  dcaPresentNotActedUpon?: boolean;
  captiveOrWhiteLabelTied?: boolean;
}
export interface GateUnfairRelationship {
  present?: boolean | null;
  fairnessReason?: FairnessReason | null;
  fcaNotificationDueDate?: string | null;
  fcaNotificationDate?: string | null;
}
export interface GateLossOrDamage {
  present?: boolean | null;
  reason?: 'no_better_deal_available' | null;
  narrative?: string;
  evidenceOption?: 'option_a_alternative_aprs' | 'option_b_broker_internal_processes' | null;
}
export interface LiabilityClosure {
  status?: RedressStatus | null;
  objectable?: boolean;
  letterType?: 'PRD' | 'RD' | null;
  fosWindowMonths?: number;
}

export interface Liability {
  schemeCase?: GateSchemeCase;
  subjectMatter?: GateSubjectMatter;
  relevantArrangement?: GateRelevantArrangement;
  exceptions?: GateExceptions;
  unfairRelationship?: GateUnfairRelationship;
  lossOrDamage?: GateLossOrDamage;
  closure?: LiabilityClosure;
  rebuttalNotes?: string;
}

// --- Calculations -----------------------------------------------------------

export interface PaymentScheduleRow {
  year: number;
  original: number;
  marketAdjusted: number;
  diff: number;
}

export interface CompensationInterestRow {
  year: number;
  rate: number;
  principalOvercharge: number;
  interest: number;
}

export interface CommissionRepaymentSummary {
  commission: number;
  interest: number;
  total: number;
}

export interface AprAdjustmentSummary {
  totalDifferential: number;
  interest: number;
  total: number;
}

export interface HybridSummary {
  commissionRepaymentTotal: number;
  aprAdjustmentTotal: number;
  average: number;
  capApplied?: string | null;
  capValue?: number | null;
}

export interface RecalculateInputs {
  scheme: 'scheme_1' | 'scheme_2';
  redress_year: number;
  redress_date?: string;
  commission_paid: number;
  commission_schedule: Array<{ year: number; amount: number }>;
  yearly_schedule: Array<{
    year: number;
    original_payment: number;
    market_adjusted_payment: number;
  }>;
  total_cost_of_credit: number;
  total_cost_of_credit_adjusted?: number | null;
  total_cost_of_credit_simple?: number | null;
  loan_amount: number;
  /** Batch G: APR the consumer paid — CONRED 5.4.6R nil-redress input. */
  agreement_apr?: number | null;
  has_dca: boolean;
  has_tied: boolean;
  schedule_source?: ScheduleSource;
}

export type ScheduleSource =
  | 'actual_settlement'
  | 'contractual'
  | 'amortisation_fallback';

export interface Calculations {
  scheduleSource?: ScheduleSource;
  // Nullable (like the computed summaries below) so "Reset to lender
  // figures" can clear the recalc-written display flags server-side.
  nilRedress?: boolean | null;
  /** Batch G: APR the consumer paid (persisted nil-redress input). */
  agreementApr?: number;
  paymentSchedule?: PaymentScheduleRow[];
  compensationInterest?: CompensationInterestRow[];
  // Computed summaries accept an explicit ``null`` so "Reset to lender
  // figures" can CLEAR them server-side (M13c — ``undefined`` values are
  // dropped by JSON.stringify, so the old reset silently sent nothing).
  commissionRepayment?: CommissionRepaymentSummary | null;
  aprAdjustment?: AprAdjustmentSummary | null;
  hybrid?: HybridSummary | null;
  johnsonEligible?: boolean | null;
  chosenRemedy?:
    | 'commission_repayment'
    | 'apr_adjustment'
    | 'hybrid'
    | string
    | null;
  chosenTotal?: number | null;
  lenderReportedTotal?: number;
  variance?: number | null;
  variancePct?: number | null;
  variationFlag?: boolean;
  /** Batch G advisory: market-adjusted APR (%) from the remedy leg (D). */
  marketAdjustedAprPct?: number | null;
  /**
   * M13d: the effective inputs the last recalculation ran with, persisted
   * by the backend so the CalculationsScreen re-seeds from them on mount.
   */
  inputs?: Partial<RecalculateInputs>;
}

// --- Offer ------------------------------------------------------------------

export type OfferDecision = 'PRD' | 'RD';
export type RemedyType = 'johnson' | 'hybrid';

// P1-8 (H13): LEGACY read-only shape. Older documents stored arrears as
// this object; the backend model (and every new save) uses a FLAT
// numeric `arrears` plus the two `arrearsSameAgreementOnly` /
// `arrearsNotUnderDispute` flags. Never SEND this shape — the backend
// now rejects it at save.
export interface OfferArrears {
  amount?: number | null;
  sameAgreementOnly?: boolean;
  notUnderDispute?: boolean;
}

export interface Offer {
  earlyMaxSettlement?: boolean;
  decision?: OfferDecision | null;
  remedyType?: RemedyType | null;
  offerAmount?: number;
  // Flat number on the wire (H13); the object union only exists so READS
  // of pre-fix documents type-check (see offerArrearsAmount helper).
  arrears?: number | OfferArrears;
  arrearsSameAgreementOnly?: boolean;
  arrearsNotUnderDispute?: boolean;
  complexLawDeduction?: number;
  vat?: number;
  totalDueToClient?: number;
}

// Extract the arrears amount from either the flat (current) or legacy
// object shape. Display-path helper — never used to build payloads.
export function offerArrearsAmount(
  offer: Offer | null | undefined,
): number | undefined {
  const a = offer?.arrears;
  if (typeof a === 'number') return a;
  if (a && typeof a === 'object') {
    return typeof a.amount === 'number' ? a.amount : undefined;
  }
  return undefined;
}

// Extract the arrears eligibility flags from either shape.
export function offerArrearsFlags(offer: Offer | null | undefined): {
  sameAgreementOnly: boolean;
  notUnderDispute: boolean;
} {
  const a = offer?.arrears;
  if (a && typeof a === 'object') {
    return {
      sameAgreementOnly: Boolean(a.sameAgreementOnly),
      notUnderDispute: Boolean(a.notUnderDispute),
    };
  }
  return {
    sameAgreementOnly: Boolean(offer?.arrearsSameAgreementOnly),
    notUnderDispute: Boolean(offer?.arrearsNotUnderDispute),
  };
}

export interface OfferBreakdown {
  offerAmount: number;
  arrears: number;
  complexLawDeduction: number;
  vat: number;
  totalDueToClient: number;
}

// --- Workflow / responses ---------------------------------------------------

export type PrdResponse = 'accept' | 'object' | 'no_response';
export type RdResponse = 'accept' | 'object_fos' | 'opt_out' | 'no_response';

// P0-4: the backend stores workflow.prdResponse / rdResponse as OBJECTS —
// an empty `{}` skeleton before any response is recorded, then
// `{value, date, notes}` once recorded. Legacy records may hold the bare
// string value. The previous string-union field type was a lie that let
// `{}` reach humaniseStatus and white-screen the Offers tab.
export interface PrdResponseRecord {
  value?: PrdResponse | null;
  date?: string;
  notes?: string;
}

export interface RdResponseRecord {
  value?: RdResponse | null;
  date?: string;
  notes?: string;
}

export interface AgreementWorkflow {
  status: RedressStatus;
  prdResponse?: PrdResponseRecord | string | null;
  rdResponse?: RdResponseRecord | string | null;
  /**
   * Batch G (PS26/3): set true when the client explicitly accepts the
   * PRD — a PRD acceptance is full-and-final and waives FOS referral
   * rights (an RD-stage acceptance deliberately does not set this).
   */
  fosRightsWaived?: boolean;
}

// --- Objection --------------------------------------------------------------

export type ObjectionReasonType =
  | 'unfair_relationship'
  | 'loss_damages'
  | 'offer_calculation'
  | 'arrears';

/**
 * H9-admin (P2-13): the SERVICE shape — a flat snake_case ``subReasons``
 * bool map whose keys come from ``OBJECTION_SUBREASONS[reasonType]``
 * (redress_models.py), plus a flat ``evidenceChecklist``. The old nested
 * camelCase blocks (``unfairRelationship``/``lossDamages``/…) were a
 * frontend invention the backend silently dropped on save.
 */
export interface Objection {
  reasonType?: ObjectionReasonType | null;
  /** Flat map keyed by OBJECTION_SUBREASONS[reasonType] (snake_case). */
  subReasons?: Record<string, boolean>;
  evidenceChecklist?: Record<string, boolean>;
  evidenceBlobRefs?: string[];
  notes?: string;
  // Service-stamped lifecycle fields (read-only on the client).
  status?: 'intent' | 'formal_submitted' | 'upheld' | 'rejected';
  registeredAt?: string;
  submittedDate?: string;
  draftSavedAt?: string;
}

// --- Dates ------------------------------------------------------------------

export interface AgreementDates {
  // P1-8 (H7): the standardised PRD anchor key (stamped by mark_prd_sent;
  // the objection deadline chain derives from it).
  provisionalRedressDate?: string;
  // LEGACY key — display fallback ONLY, never a deadline anchor.
  prdDate?: string;
  objectionDueDate?: string;
  objectionDate?: string;
  acknowledgementDueDate?: string;
  acknowledgementDate?: string;
  formalObjectionDueDate?: string;
  formalObjectionDate?: string;
  responseDueDate?: string;
  responseDate?: string;
  finalRedressDate?: string;
  moneyDueDate?: string;
  paymentSentToClientDate?: string;
  paymentReceivedDate?: string;
  statutoryBillSentDate?: string;
  fosDeadline?: string;
  // NOTE: the draft-rules §9.12 interestSuspendedFrom/To keys are GONE —
  // the PS26/3 final rules have no interest suspension (Batch G).
  // P0-1: stamped when the client explicitly accepts the PRD.
  acceptanceDate?: string;
  paymentDueDate?: string;
  // P1-6: stamped by mark_rd_sent (RD issued; response due +6 months).
  rdSentDate?: string;
  rdResponseDueDate?: string;
  /**
   * Batch G (CONRED 5.4.37R(6)): stamped on PRD lapse — the lender must
   * issue an RD confirming the PRD within 1 month of the objection
   * window expiring. Chased via the ``rd_expected`` reminder kind.
   */
  rdExpectedByDate?: string;
}

// --- Lender details + Agreement ---------------------------------------------

export interface LenderDetails {
  lenderName?: string;
  lenderRef?: string;
  agreementNumber?: string;
}

// P1-6: pre-RD snapshot of the PRD-stage offer + calculation, appended by
// mark_rd_sent so entering RD figures never destroys the PRD record.
export interface OfferHistoryEntry {
  stage: 'prd' | 'rd';
  offer?: Offer;
  calculation?: Calculations;
  snapshotAt?: string;
}

export interface Agreement {
  agreementId: string;
  lenderDetails?: LenderDetails;
  creditStartDate?: string;
  scheme: RedressScheme;
  liability: Liability;
  calculations: Calculations;
  offer: Offer;
  workflow: AgreementWorkflow;
  objection: Objection;
  fosReferral?: {
    referredDate?: string;
    reference?: string;
    letterBlobRef?: string;
  };
  dates: AgreementDates;
  // P1-5: the client response link is minted PER-AGREEMENT (prd or rd
  // stage) by mark_prd_sent / mark_rd_sent.
  documents?: {
    responseLink?: string;
  };
  offerHistory?: OfferHistoryEntry[];
}

export interface Vehicle {
  make?: string;
  model?: string;
  registration?: string;
}

// --- Ledger -----------------------------------------------------------------

export type LedgerEntryType =
  | 'offer_received'
  | 'arrears_deducted'
  | 'cl_deduction'
  | 'cl_fee_shortfall'
  | 'statutory_bill'
  | 'vat'
  | 'paid_to_client'
  | 'late_interest'
  | 'adjustment';

export type LedgerDirection = 'in' | 'out';

export interface LedgerEntry {
  id: string;
  ts: string;
  type: LedgerEntryType;
  label: string;
  amount: number;
  direction: LedgerDirection;
  reference?: string;
  createdBy?: string;
  reversedEntryId?: string;
  reversedAt?: string;
  reversedBy?: string;
  agreementId?: string | null;
  chaseRemainder?: boolean;
}

export interface LedgerTotals {
  totalReceivedFromLender: number;
  totalArrearsDeducted: number;
  totalClFees: number;
  totalClFeeShortfall: number;
  totalVat: number;
  totalLateInterest: number;
  totalPaidToClient: number;
  balance: number;
  lastEntryAt: string | null;
}

export interface LedgerResponse {
  entries: LedgerEntry[];
  totals: LedgerTotals;
}

export interface LedgerMutationResponse {
  case: RedressCase;
  totals: LedgerTotals;
}

// --- Firm-wide dashboard ----------------------------------------------------

export interface FirmRedressSummary {
  byStatus: Record<string, number>;
  byScheme: Record<string, number>;
  totals: {
    expectedReceivableFromLenders: number;
    totalReceivedFromLenders: number;
    totalPaidToClients: number;
    totalClFees: number;
    totalVatCollected: number;
    totalLateInterest: number;
    totalPendingPayout: number;
  };
  ageing: {
    prdLapsingIn7d: number;
    paymentsOverdue: number;
    fosDeadlineWithin30d: number;
  };
  caseCount: number;
  // P1-9: byStatus/byScheme count AGREEMENTS (the workflow unit);
  // agreementCount is their denominator. Optional only for back-compat
  // with a not-yet-redeployed backend.
  agreementCount?: number;
  generatedAt: string;
}

// --- Closure response -------------------------------------------------------

/**
 * The closure outcome dict the backend's ``evaluate_liability`` emits
 * (``_closure()`` in redress_service.py).
 */
export interface LiabilityClosureResult {
  status: RedressStatus;
  letterType?: 'PRD' | 'RD' | null;
  objectable?: boolean;
  fosWindowMonths?: number;
}

/**
 * M13a: the ACTUAL /liability/close response shape —
 * ``{case, closure, highValueHold}``. The old frontend type declared
 * ``closureStatus``/``closureReason`` which the backend never returned,
 * so the UI displayed "Agreement closed (undefined)".
 */
export interface CloseLiabilityResponse {
  case: RedressCase;
  closure: LiabilityClosureResult | null;
  highValueHold: boolean;
}

// --- RedressCase (vehicle-level) --------------------------------------------

export interface RedressCase {
  id: string;
  claimId: string;
  proclaimReference: string;
  vehicle: Vehicle;
  caseStatus: string;
  agreements: Agreement[];
  otherInfo: Record<string, unknown>;
  ledger: LedgerEntry[];
  documents: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
  updatedBy?: string | null;
}

// --- Bulk upload (Milestone 7) ----------------------------------------------

export interface BulkUploadParsedRow {
  row_number: number;
  claimReference: string;
  proclaimReference?: string | null;
  data: Record<string, unknown>;
  errors: string[];
  warnings: string[];
}

export interface BulkUploadPreview {
  totalRows: number;
  validRows: number;
  errorRows: number;
  summaryErrors: string[];
  rows: BulkUploadParsedRow[];
}

export interface BulkUploadCommitResult {
  applied: number;
  skipped: number;
  failures: Array<{
    claimReference: string;
    rowNumber?: number;
    error: string;
  }>;
  preview?: {
    totalRows: number;
    validRows: number;
    errorRows: number;
  };
}

// --- API client --------------------------------------------------------------

// Case-level URL helper (used by case-scoped endpoints and bulk upload).
function caseUrl(claimId: string, caseId: string, suffix: string): string {
  return `/redress/cases/${encodeURIComponent(claimId)}/${encodeURIComponent(caseId)}${suffix}`;
}

// Agreement-scoped URL helper — every per-agreement mutation routes through this.
function agreementUrl(
  claimId: string,
  caseId: string,
  agreementId: string,
  suffix: string,
): string {
  return `/redress/cases/${encodeURIComponent(claimId)}/${encodeURIComponent(
    caseId,
  )}/agreements/${encodeURIComponent(agreementId)}${suffix}`;
}

/**
 * M14: thrown when the /redress routes don't exist on the server
 * (``REDRESS_ENABLED=false`` — the blueprint isn't registered, so EVERY
 * redress URL 404s with the app's generic ``{"error": "Not Found"}``).
 * The frontend flag defaults ON while the backend flag defaults OFF, so
 * without this distinction the 404-normaliser misreported
 * "backend disabled" as "no redress case yet → Create".
 */
export class RedressBackendDisabledError extends Error {
  constructor() {
    super('Redress module is not enabled on the server');
    this.name = 'RedressBackendDisabledError';
  }
}

// The by-claim route's own "no case" 404 body (routes/redress.py) — any
// OTHER 404 on this URL means the route itself doesn't exist. Detection
// prefers the structured ``code`` (F3 polish); the exact-message match is
// the back-compat fallback for older backends that only send the string.
const NO_CASE_404_CODE = 'no_redress_case';
const NO_CASE_404_MESSAGE = 'No redress case for this claim';

/**
 * Normalise a HTTP 404 on "no case for this claim" into a tidy ``null``
 * so callers can render a "Create case" affordance without catching.
 *
 * M14: a 404 whose body is NOT the route's own "no case" error means the
 * redress blueprint isn't registered (backend ``REDRESS_ENABLED=false``)
 * — that throws ``RedressBackendDisabledError`` instead of returning
 * ``null``, so the UI can say "module disabled" rather than offering a
 * Create button that can only 404.
 */
async function getByClaim(claimId: string): Promise<RedressCase | null> {
  try {
    const res = await client.get<RedressCase>(
      `/redress/cases/by-claim/${encodeURIComponent(claimId)}`,
    );
    return res.data;
  } catch (err: unknown) {
    const response = (err as {
      response?: { status?: number; data?: { error?: string; code?: string } };
    })?.response;
    if (response?.status === 404) {
      if (
        response?.data?.code === NO_CASE_404_CODE ||
        response?.data?.error === NO_CASE_404_MESSAGE
      ) {
        return null;
      }
      throw new RedressBackendDisabledError();
    }
    throw err;
  }
}

async function createForClaim(claimId: string): Promise<RedressCase> {
  const res = await client.post<RedressCase>('/redress/cases', { claimId });
  return res.data;
}

/**
 * GET /redress/cases -- list cases, optionally filtered. Used by the
 * FOS referrals list (M9) which filters on
 * ``status='rejected_fos_referral'``.
 */
async function listCases(
  params?: { status?: RedressStatus; scheme?: RedressScheme; limit?: number },
): Promise<{ cases: RedressCase[]; count: number }> {
  const res = await client.get<{ cases: RedressCase[]; count: number }>(
    '/redress/cases',
    { params },
  );
  return res.data;
}

// --- Per-agreement mutation endpoints ---------------------------------------

async function saveLiability(
  claimId: string,
  caseId: string,
  agreementId: string,
  patch: Liability,
): Promise<RedressCase> {
  const res = await client.put<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/liability'),
    patch,
  );
  return res.data;
}

async function closeLiability(
  claimId: string,
  caseId: string,
  agreementId: string,
  patch: Liability,
): Promise<CloseLiabilityResponse> {
  const res = await client.post<CloseLiabilityResponse>(
    agreementUrl(claimId, caseId, agreementId, '/liability/close'),
    patch,
  );
  return res.data;
}

async function transition(
  claimId: string,
  caseId: string,
  agreementId: string,
  newStatus: RedressStatus,
): Promise<RedressCase> {
  const res = await client.post<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/transition'),
    { newStatus },
  );
  return res.data;
}

async function downloadClosureLetter(
  claimId: string,
  caseId: string,
  agreementId: string,
): Promise<Blob> {
  const res = await client.get<Blob>(
    agreementUrl(claimId, caseId, agreementId, '/closure-letter'),
    { responseType: 'blob' },
  );
  return res.data;
}

async function saveCalculations(
  claimId: string,
  caseId: string,
  agreementId: string,
  patch: Calculations,
): Promise<RedressCase> {
  const res = await client.put<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/calculations'),
    patch,
  );
  return res.data;
}

async function recalculate(
  claimId: string,
  caseId: string,
  agreementId: string,
  inputs: RecalculateInputs,
  lenderReportedTotal: number,
): Promise<RedressCase> {
  const res = await client.post<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/recalculate'),
    { inputs, lenderReportedTotal },
  );
  return res.data;
}

async function saveOffer(
  claimId: string,
  caseId: string,
  agreementId: string,
  patch: Offer,
): Promise<RedressCase> {
  const res = await client.put<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/offer'),
    patch,
  );
  return res.data;
}

async function computeOfferBreakdown(
  claimId: string,
  caseId: string,
  agreementId: string,
  offerAmount: number,
  arrears: number,
): Promise<OfferBreakdown> {
  const res = await client.post<OfferBreakdown>(
    agreementUrl(claimId, caseId, agreementId, '/offer/compute'),
    { offerAmount, arrears },
  );
  return res.data;
}

async function saveDates(
  claimId: string,
  caseId: string,
  agreementId: string,
  patch: Partial<AgreementDates>,
): Promise<RedressCase> {
  const res = await client.put<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/dates'),
    patch,
  );
  return res.data;
}

async function markPrdSent(
  claimId: string,
  caseId: string,
  agreementId: string,
  prdDate?: string,
): Promise<RedressCase> {
  const res = await client.post<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/prd/send'),
    prdDate ? { prdDate } : {},
  );
  return res.data;
}

/**
 * P1-6: mark the RD as issued to the client for a single agreement.
 * Backend guards the transition into rd_sent_to_client FIRST (409
 * otherwise) — valid from awaiting_rd, objection_submitted or (Batch-G
 * review fix 2) client_objecting; it then
 * snapshots the PRD offer to offerHistory, stamps rdSentDate /
 * rdResponseDueDate (+6 months) and mints the 190-day RD response link.
 */
async function markRdSent(
  claimId: string,
  caseId: string,
  agreementId: string,
  rdDate?: string,
): Promise<RedressCase> {
  const res = await client.post<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/rd/send'),
    rdDate ? { rdDate } : {},
  );
  return res.data;
}

async function recordClientResponse(
  claimId: string,
  caseId: string,
  agreementId: string,
  response: PrdResponse | RdResponse,
  options?: { date?: string; notes?: string },
): Promise<RedressCase> {
  const res = await client.post<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/response'),
    {
      response,
      ...(options?.date ? { date: options.date } : {}),
      ...(options?.notes !== undefined ? { notes: options.notes } : {}),
    },
  );
  return res.data;
}

/**
 * Atomic client-rejection + FOS referral. Calls the backend wrapper endpoint
 * that records the 'rejected' response AND generates + persists the FOS
 * referral letter in one round trip.
 */
async function rejectAndReferToFos(
  claimId: string,
  caseId: string,
  agreementId: string,
  rejectionReason: string,
  options?: { date?: string },
): Promise<RedressCase> {
  const res = await client.post<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/reject-and-refer'),
    {
      rejectionReason,
      ...(options?.date ? { date: options.date } : {}),
    },
  );
  return res.data;
}

async function acknowledgeObjection(
  claimId: string,
  caseId: string,
  agreementId: string,
  receivedAt?: string,
): Promise<RedressCase> {
  const res = await client.post<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/objection/acknowledge'),
    receivedAt ? { acknowledgementDate: receivedAt } : {},
  );
  return res.data;
}

async function markFdReceived(
  claimId: string,
  caseId: string,
  agreementId: string,
  fdDate?: string,
): Promise<RedressCase> {
  const res = await client.post<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/fd'),
    fdDate ? { fdDate } : {},
  );
  return res.data;
}

async function markPaid(
  claimId: string,
  caseId: string,
  agreementId: string,
  options?: { paymentDate?: string; bankReference?: string },
): Promise<RedressCase> {
  const res = await client.post<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/paid'),
    {
      ...(options?.paymentDate ? { paymentDate: options.paymentDate } : {}),
      ...(options?.bankReference
        ? { bankReference: options.bankReference }
        : {}),
    },
  );
  return res.data;
}

// --- Ledger (per-agreement) -------------------------------------------------

async function getLedger(
  claimId: string,
  caseId: string,
  agreementId: string,
): Promise<LedgerResponse> {
  const res = await client.get<LedgerResponse>(
    agreementUrl(claimId, caseId, agreementId, '/ledger'),
  );
  return res.data;
}

async function addLedgerEntry(
  claimId: string,
  caseId: string,
  agreementId: string,
  entry: {
    type: LedgerEntryType;
    amount: number;
    direction: LedgerDirection;
    label?: string;
    reference?: string;
  },
): Promise<LedgerMutationResponse> {
  const res = await client.post<LedgerMutationResponse>(
    agreementUrl(claimId, caseId, agreementId, '/ledger'),
    entry,
  );
  return res.data;
}

async function removeLedgerEntry(
  claimId: string,
  caseId: string,
  agreementId: string,
  entryId: string,
): Promise<LedgerMutationResponse> {
  const res = await client.delete<LedgerMutationResponse>(
    agreementUrl(claimId, caseId, agreementId, `/ledger/${encodeURIComponent(entryId)}`),
  );
  return res.data;
}

// --- Letter downloads (per-agreement) ---------------------------------------

async function downloadPrdLetter(
  claimId: string,
  caseId: string,
  agreementId: string,
  responseLink?: string,
): Promise<Blob> {
  const qs = responseLink
    ? `?responseLink=${encodeURIComponent(responseLink)}`
    : '';
  const res = await client.get<Blob>(
    agreementUrl(claimId, caseId, agreementId, `/prd-letter${qs}`),
    { responseType: 'blob' },
  );
  return res.data;
}

/** P1-6: RD (Redress Determination) client letter — placeholder copy. */
async function downloadRdLetter(
  claimId: string,
  caseId: string,
  agreementId: string,
  responseLink?: string,
): Promise<Blob> {
  const qs = responseLink
    ? `?responseLink=${encodeURIComponent(responseLink)}`
    : '';
  const res = await client.get<Blob>(
    agreementUrl(claimId, caseId, agreementId, `/rd-letter${qs}`),
    { responseType: 'blob' },
  );
  return res.data;
}

async function downloadFdLetter(
  claimId: string,
  caseId: string,
  agreementId: string,
): Promise<Blob> {
  const res = await client.get<Blob>(
    agreementUrl(claimId, caseId, agreementId, '/fd-letter'),
    { responseType: 'blob' },
  );
  return res.data;
}

async function downloadFosLetter(
  claimId: string,
  caseId: string,
  agreementId: string,
  reason: string,
): Promise<Blob> {
  const res = await client.get<Blob>(
    agreementUrl(claimId, caseId, agreementId, `/fos-letter?reason=${encodeURIComponent(reason)}`),
    { responseType: 'blob' },
  );
  return res.data;
}

// --- Objection endpoints (M6-3) ---------------------------------------------

async function saveObjection(
  claimId: string,
  caseId: string,
  agreementId: string,
  // F3 polish: the transitioning intake also accepts an optional
  // ``objectionDate`` (backdated objections — the route stamps today
  // when omitted). It lives on the agreement's DATES, not the objection
  // block, hence the intersection rather than a new Objection field.
  patch: Objection & { objectionDate?: string },
): Promise<RedressCase> {
  const res = await client.post<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/objection'),
    patch,
  );
  return res.data;
}

/**
 * P2-13 (H9-admin): non-transitioning DRAFT save. The service maps
 * ``reasonType`` / flat snake_case ``subReasons`` / ``evidenceChecklist``
 * / ``notes`` onto the agreement's objection block WITHOUT touching
 * ``workflow.status`` (the transitioning intake stays ``saveObjection``).
 */
async function saveObjectionDraft(
  claimId: string,
  caseId: string,
  agreementId: string,
  patch: {
    reasonType?: ObjectionReasonType | null;
    subReasons?: Record<string, boolean>;
    evidenceChecklist?: Record<string, boolean>;
    notes?: string;
  },
): Promise<RedressCase> {
  const res = await client.patch<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/objection/draft'),
    patch,
  );
  return res.data;
}

// Objection-upheld loop: firm accepts the objection and re-issues a PRD.
async function reissuePrd(
  claimId: string,
  caseId: string,
  agreementId: string,
): Promise<RedressCase> {
  const res = await client.post<RedressCase>(
    agreementUrl(claimId, caseId, agreementId, '/objection/uphold'),
    {},
  );
  return res.data;
}

// --- Firm-wide dashboard ----------------------------------------------------

async function getFirmRedressSummary(): Promise<FirmRedressSummary> {
  const res = await client.get<FirmRedressSummary>(
    '/redress/dashboard/summary',
  );
  return res.data;
}

// --- Bulk upload (Milestone 7) ----------------------------------------------

/**
 * Download the canonical XLSX template and trigger a browser download.
 *
 * The endpoint returns an ``attachment`` so we pipe the bytes into a
 * temp blob URL rather than redirecting (which would bypass the
 * Authorization header baked into the axios instance).
 */
async function downloadBulkUploadTemplate(): Promise<void> {
  const res = await client.get<Blob>('/redress/bulk-upload/template', {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'redress-bulk-upload-template.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function previewBulkUpload(file: File): Promise<BulkUploadPreview> {
  const form = new FormData();
  form.append('file', file);
  const res = await client.post<BulkUploadPreview>(
    '/redress/bulk-upload/preview',
    form,
  );
  return res.data;
}

async function commitBulkUpload(
  file: File,
  skipErrors: boolean,
): Promise<BulkUploadCommitResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('confirm', 'true');
  if (skipErrors) {
    form.append('skipErrors', 'true');
  }
  const res = await client.post<BulkUploadCommitResult>(
    '/redress/bulk-upload/commit',
    form,
  );
  return res.data;
}

export const redressApi = {
  // Case-level (unchanged):
  getByClaim,
  createForClaim,
  listCases,
  getFirmRedressSummary,
  downloadBulkUploadTemplate,
  previewBulkUpload,
  commitBulkUpload,
  // Per-agreement mutations:
  saveLiability,
  closeLiability,
  transition,
  downloadClosureLetter,
  saveCalculations,
  recalculate,
  saveOffer,
  computeOfferBreakdown,
  saveDates,
  markPrdSent,
  markRdSent,
  recordClientResponse,
  rejectAndReferToFos,
  acknowledgeObjection,
  markFdReceived,
  markPaid,
  downloadPrdLetter,
  downloadRdLetter,
  downloadFdLetter,
  downloadFosLetter,
  getLedger,
  addLedgerEntry,
  removeLedgerEntry,
  // Objection (M6-3):
  saveObjection,
  saveObjectionDraft,
  reissuePrd,
};
